import { useCallback, useEffect, useRef, useState } from 'react'

export type Async<T> =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; data: T }

/**
 * A deliberately small data hook. No cache, no query library: the
 * screens here are few and each one loads once. If this app grows a
 * dozen more screens, swap it for TanStack Query rather than growing
 * this file.
 */
export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): Async<T> & { reload: () => void } {
  const [result, setResult] = useState<Async<T>>({ state: 'loading' })
  const [nonce, setNonce] = useState(0)
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    const controller = new AbortController()
    let live = true
    setResult({ state: 'loading' })

    loadRef
      .current(controller.signal)
      .then((data) => {
        if (live) setResult({ state: 'ready', data })
      })
      .catch((error: unknown) => {
        if (!live || controller.signal.aborted) return
        setResult({
          state: 'error',
          message: error instanceof Error ? error.message : String(error),
        })
      })

    return () => {
      live = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { ...result, reload }
}
